import isEmpty from 'lodash/isEmpty';

import type {ColumnFiltersState} from '@tanstack/react-table';
import type {AvailableFilters, Operator} from '@linx-security/table';
import type {
  FilterFunction,
  FilterMultiSelect,
  FilterType,
  FilterValueWithOperator,
} from '@lib/types/table-filter-types';

/**
 * This is a helper function that formats the table filter parameters
 * Currently a complex technical debt, we should refactor this function in conjunction with the BE
 */
export const transformFilters = ({
  params,
  availableFilters = {},
  isTraversal = false,
}: {
  params: ColumnFiltersState;
  availableFilters?: Partial<AvailableFilters>;
  isTraversal?: boolean;
}) => {
  const query = params
    // Remove empty filters
    .filter(filter => !isEmpty(filter))
    // Create the query array
    .map(d => {
      const {id, value: filterValue, toQuery} = d;
      if (toQuery) {
        const {operator} = d as any;
        if (!filterValue) {
          return undefined;
        }
        return toQuery(
          (filterValue as FilterValueWithOperator)?.value,
          operator ?? (filterValue as FilterValueWithOperator)?.operator,
          {
            definition: d as any,
            ctx: {...(d?.extra ?? {})},
          },
        );
      }

      const availableFilter = availableFilters?.[id];
      const {value, operator} = filterValue as FilterValueWithOperator;

      // If we define the available filter
      if (availableFilter) {
        // Grab the filter & type from available filters
        const {filter, type} = availableFilter;
        if (typeof filter === `function`) {
          return (filter as FilterFunction)(filterValue as FilterValueWithOperator);
        } else if (type === FilterTypes.Boolean) {
          const filterMap = filter as Record<string, boolean>;
          const filterValue = filterMap[value];

          if (filterValue !== undefined && !Array.isArray(filterMap)) {
            return {[id]: filterValue};
          }

          // Handle string boolean values ('true'/'false')
          const lowerValue = String(value).toLowerCase();
          if (lowerValue === `true` || lowerValue === `yes`) {
            return {[id]: {[operator as string]: true}};
          }
          if (lowerValue === `false` || lowerValue === `no`) {
            return {[id]: {[operator as string]: false}};
          }

          // Legacy fallback for '0'/'1' values
          const filterOperator =
            operator === (`$ne` as Operator)
              ? Number(value) === 0
                ? `$ne`
                : `$eq`
              : Number(value) === 0
                ? `$eq`
                : `$ne`;

          return {[id]: {[filterOperator]: true}};
        } else if (type === FilterTypes.Number) {
          return {[id]: {[operator as string]: Number(value)}};
        } else if (type === FilterTypes.MultiSelect) {
          const filterKeys = Array.isArray(value) ? value.join(`,`) : String(value);
          const filterValues = getValuesFromKeys(filter as FilterMultiSelect, filterKeys);
          const filterValuesStr = filterValues.join(`,`);
          if (operator === `$elemMatch`) {
            return {
              $or: (JSON.parse(`[${filterValuesStr}]`) as Array<Record<string, any>>).map(filterValuesStr => ({
                [id]: {$elemMatch: filterValuesStr},
              })),
            };
          }

          if (operator === `$notElemMatch`) {
            return {
              $and: (JSON.parse(`[${filterValuesStr}]`) as Array<Record<string, any>>).map(filterValuesStr => ({
                [id]: {$notElemMatch: filterValuesStr},
              })),
            };
          }

          if (operator === `$notEmpty`) {
            return {[id]: operator};
          }

          if (operator === `$nin`) {
            return {[id]: {$nin: filterValues}};
          }

          return {[id]: {[operator as string]: operator === `$in` ? filterValues : (filterValuesStr as string)}};
        } else if (operator !== `$search`) {
          // Days-based operators expect numeric values
          if (
            operator === `$daysAgo` ||
            operator === `$notDaysAgo` ||
            operator === `$daysFromNow` ||
            operator === `$workingDaysFromNow`
          ) {
            return {[id]: {[operator as string]: Number(value)}};
          }
          return {[id]: {[operator as string]: value}};
        }
      }

      if (typeof filterValue === `string`) {
        return {[id]: filterValue};
      }

      // Handle days-based operators even without availableFilter definition
      // These operators always need numeric values
      if (
        operator === `$daysAgo` ||
        operator === `$notDaysAgo` ||
        operator === `$daysFromNow` ||
        operator === `$workingDaysFromNow`
      ) {
        return {[id]: {[operator as string]: Number(value)}};
      }

      // Fallback to search
      return {[id]: {$search: value}};
    })
    .filter(Boolean);

  if (isTraversal && query.length > 0) {
    // Group queries by their top-level field
    // input: [{ account: { name: {eq: 'test'} } }, { account: { name: {$eq: true } } }]
    // output: { account: { $and: [{name: {eq: 'test'}}, {name: {eq: true}}] } }
    const groupedQueries = query.reduce(
      (acc, curr) => {
        if (!curr || !acc) {
          return acc;
        }
        const fields = Object.keys(curr);
        if (fields.length === 0) {
          return acc;
        }
        const field = fields[0];
        if (!acc[field]) {
          acc[field] = [];
        }
        acc[field].push(curr[field]);
        return acc;
      },
      {} as Record<string, Array<any>>,
    );

    // Merge grouped queries with $and operator
    const mergedQuery = Object.entries(groupedQueries ?? {}).reduce(
      (acc, [field, queries]) => {
        acc[field] = queries.length > 1 ? {$and: queries} : queries[0];
        return acc;
      },
      {} as Record<string, any>,
    );

    return mergedQuery;
  }

  return params.length === 0 || query.length === 0
    ? undefined
    : {
        $and: query.filter(v => !isEmpty(v)),
      };
};

const getValuesFromKeys = (obj: Record<string, any>, keysString: string) => {
  const keys = keysString.split(`,`).map(key => key.trim());
  return keys.map(key => obj[key]);
};

export const FilterTypes: Record<string, FilterType> = {
  Text: `text`,
  Date: `date`,
  Number: `number`,
  Boolean: `boolean`,
  MultiSelect: `multiselect`,
} as const;
